import React, { useEffect, useLayoutEffect } from 'react';
import { useRef, useState, useCallback } from 'react';
import { Map, Marker } from 'react-map-gl';
import DeckGL from '@deck.gl/react';
import { GeoJsonLayer } from '@deck.gl/layers';
import DrawingToolbar from '../Drawing/DrawingToolbar';
import { handleMapClick } from '../../utils/MapClickHandlers';
import {
    handleCompleteDrawingLine,
    handleCompleteDrawingMultiLine,
    handleCompleteDrawingPolygon,
    handleCompleteDrawingMultiPolygon,
    handleMeasureDrawing,
    handleDrawPoint,
    handleDrawMultiPoint,
    handleDrawLine,
    handleDrawMultiLine,
    handleDrawPolygon,
    handleDrawMultiPolygon
} from '../sketch/DrawMethods';
import { mouseMove, rightClick } from '../mouse_controls/mouse_controls';
import { analyzeRoi, cancelSelectedRoi } from '../RegionOfInterest/AnalyzeRegionOfInterest';
import { createLayers } from '../../utils/CreateLayers';
import AttributeForm from '../sketch/AttributeForm';
import { handleSave as handleSave } from '../../utils/SaveHandler';
import HoverInfo from '../../utils/hoverInfo/HoverInfo';
import MapSearch from '../../utils/Search/MapSearch';
import { FaMapMarkerAlt } from 'react-icons/fa';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MainContent.css';
import CompareLayers from '../Compare/CompareLayers';
import { debounce } from 'lodash';
import ZoomControl from '../../utils/ZoomControl/ZoomControl';
import { useAccessRights } from '../../utils/LoginForm/Auth/useAccessRights';
import { EditableGeoJsonLayer } from 'nebula.gl';
import { ModifyMode } from '@nebula.gl/edit-modes';
import { MAPBOX_TOKEN } from '../../config';
import {
    createRectangleGeoJson,
    createCircleGeoJson,
    pickFeaturesInRectangle,
    pickFeaturesInCircle
} from '../../utils/selection/SelectionUtils';

const MainContent = ({
    isRightSidebarCollapsed,
    wfsLayers,
    wmsLayers,
    setWfsLayers,
    hoverInfo,
    setSelectedFeature,
    activeMode,
    setActiveMode,
    setMeasurementResult,
    measurementResult,
    clickInfo,
    setClickInfo,
    substationOptions,
    setRegionStubovi,
    setRegionTrafostanice,
    setRegionVodovi,
    mapStyle,
    viewState,
    setViewState,
    lasLayers,
    layerInfo,
    currentDrawing,
    currentMeasureDrawing,
    setCurrentDrawing,
    setCurrentMeasureDrawing,
    setPopupInfo,
    setLayerEPSG,
    setIsCreated,
    setLayerName,
    setMultiSelectedFeatures,
    multipleSelectedFeatureLayer,
    isCompareMode,
    checkedLayers,
    isUpdated,
    isDeleted,
    isMenuOpen,
    isLeftSidebarCollapsed,
    isTrafoActive,
    isExpanded,
    ojInfo,
    setHoverInfo,
    handleUpdateGeometryWrapper,
    mapRef,
    deckRef,
    handleClearSelectedFeatures
}) => {
    const mainContentRef = useRef(null);
    const verteksRef = useRef(null);
    const animationFrameRef = useRef(null);

    const [points, setPoints] = useState([]);
    const [multiPoints, setMultiPoints] = useState([]);
    const [lines, setLines] = useState([]);
    const [multiLines, setMultiLines] = useState([]);
    const [polygons, setPolygons] = useState([]);
    const [multiPolygons, setMultiPolygons] = useState([]);
    const [isAttributeFormOpen, setAttributeFormOpen] = useState(false);
    const [currentCoordinate, setCurrentCoordinate] = useState(null);
    const [snappedVertex, setSnappedVertex] = useState(null);
    const [snapLayer, setSnapLayer] = useState(null);
    const [terrainPointsLayer, setTerrainPointsLayer] = useState(null);

    const [attributeValues, setAttributeValues] = useState({});
    const [extractedLayerName, setExtractedLayerName] = useState();
    const [geometryName, setGeometryName] = useState();
    const [layerCRS, setLayerCRS] = useState();
    const [layerId, setLayerId] = useState();
    const [manualCoordinates, setManualCoordinates] = useState();
    const [inputUrl, setInputUrl] = useState();
    const [refresh, setRefresh] = useState();
    const [featureProperties, setFeatureProperties] = useState([]);
    const [geometryType, setGeometryType] = useState('');
    const [addedCoordinate, setAddedCoordinate] = useState([]);
    const [marker, setMarker] = useState();
    const [deckKey, setDeckKey] = useState(0);
    const [terrainProfilePoints, setTerrainProfilePoints] = useState([]);
    const [measureLayer, setMeasureLayer] = useState(null);
    const [drawenLayers, setDrawenLayers] = useState([]);
    const [isLayersExists, setIsLayersExists] = useState(false);
    const [editFeatures, setEditFeatures] = useState(null);
    const [editMode, setEditMode] = useState(null);
    const [showSearch, setShowSearch] = useState(false);
    const [selectionDrag, setSelectionDrag] = useState(null);

    const { accessProfile } = useAccessRights();
    const hasAnyAccess = Object.values(accessProfile).some(item => item.value === true);

    const isAreaSelectMode = activeMode === "selectRectangle" || activeMode === "selectCircle";

    const handleAreaSelectionClick = (info) => {
        if (!isAreaSelectMode || typeof info?.x !== "number" || typeof info?.y !== "number") {
            return false;
        }

        const point = {
            x: info.x,
            y: info.y,
        };

        if (!selectionDrag) {
            setSelectionDrag({
                type: activeMode,
                start: point,
                end: point,
            });

            return true;
        }

        const finalDrag = {
            ...selectionDrag,
            end: point,
        };

        let selected = [];

        if (finalDrag.type === "selectRectangle") {
            selected = pickFeaturesInRectangle(deckRef, finalDrag.start, finalDrag.end);
        }

        if (finalDrag.type === "selectCircle") {
            selected = pickFeaturesInCircle(deckRef, finalDrag.start, finalDrag.end);
        }

        if (selected.length > 0) {
            setSelectedFeature(selected);
            setMultiSelectedFeatures(selected);
        } else {
            setSelectedFeature(null);
            setMultiSelectedFeatures([]);
        }

        setSelectionDrag(null);

        return true;
    };

    const handleAreaSelectionHover = (info) => {
        if (!isAreaSelectMode || !selectionDrag || typeof info?.x !== "number" || typeof info?.y !== "number") {
            return;
        }

        setSelectionDrag(prev => ({
            ...prev,
            end: {
                x: info.x,
                y: info.y,
            },
        }));
    };

    const hoverLayer = new GeoJsonLayer({
        id: 'hover-layer',
        data: hoverInfo?.object?.type ? hoverInfo.object : { type: "FeatureCollection", features: [] },
        pickable: false,
        stroked: true,
        filled: true,
        visible: true,
        getFillColor: [0, 255, 0, 80],
        getLineColor: [50, 205, 50, 255],
        getPointRadius: 2,
        getLineWidth: 3,
    });

    const handleClickOnMap = (info) => {
        window.dispatchEvent(new Event("close-toolbar-popups"));

        if (handleAreaSelectionClick(info)) {
            return;
        }

        if (clickInfo) {
            setClickInfo(null);
        }

        const mapInstance = mapRef.current;
        const lngLat = info.coordinate;

        if (mapInstance && mapInstance.queryTerrainElevation) {
            mapInstance.queryTerrainElevation(lngLat);
        } else {
            console.error("Map instance ili queryTerrainElevation nisu dostupni.");
        }

        handleMapClick(
            info,
            activeMode,
            wfsLayers,
            currentMeasureDrawing,
            setCurrentMeasureDrawing,
            handleMeasureDrawing,
            setLayerEPSG
        );

        const { x, y } = info;
        const pickedFeatures = deckRef.current?.pickMultipleObjects
            ? deckRef.current.pickMultipleObjects({ x, y })
            : [];

        if (activeMode === "select" || activeMode === "update" || activeMode === "delete") {
            const disallowedPrefixes = ['pgsql', 'kml', 'gml', 'geojson', 'shp', 'las', 'dx'];

            const clickedFeatures = Array.isArray(pickedFeatures)
                ? pickedFeatures
                    .map((f) => f.object)
                    .filter((f) => f?.id && !disallowedPrefixes.some(prefix => f.id.startsWith(prefix)))
                : [];

            setSelectedFeature((prev) => {
                const prevFeatures = Array.isArray(prev) ? prev : [];
                const clickedIds = new Set(clickedFeatures.map(f => f.id));
                const updated = prevFeatures.filter(f => !clickedIds.has(f.id));

                return updated.length === prevFeatures.length
                    ? [...updated, ...clickedFeatures]
                    : updated;
            });
        }

        if (activeMode === "selectMultiple") {
            setMultiSelectedFeatures(prev => {
                const disallowedPrefixes = ['pgsql', 'kml', 'gml', 'geojson', 'shp', 'las', 'dx'];

                const newFeatures = pickedFeatures
                    .map(f => f.object)
                    .filter(f => f?.id && !disallowedPrefixes.some(prefix => f.id.startsWith(prefix)));

                const updated = [...prev];

                newFeatures.forEach(f => {
                    const index = updated.findIndex(existing => existing.id === f.id);

                    if (index > -1) {
                        updated.splice(index, 1);
                    } else {
                        updated.push(f);
                    }
                });

                return updated;
            });
        }

        if (activeMode === "terrainCrossSection") {
            const terrainMapInstance = mapRef.current;
            const terrainLngLat = info.coordinate;

            if (terrainMapInstance && terrainMapInstance.queryTerrainElevation) {
                const elevation = terrainMapInstance.queryTerrainElevation(terrainLngLat);

                setTerrainProfilePoints(prev => {
                    const TOLERANCE = 0.00015;

                    const alreadyExists = prev.some(p =>
                        Math.abs(p.lng - terrainLngLat[0]) < TOLERANCE &&
                        Math.abs(p.lat - terrainLngLat[1]) < TOLERANCE
                    );

                    if (alreadyExists) {
                        return prev.filter(p =>
                            !(Math.abs(p.lng - terrainLngLat[0]) < TOLERANCE &&
                                Math.abs(p.lat - terrainLngLat[1]) < TOLERANCE)
                        );
                    }

                    return [...prev, { lng: terrainLngLat[0], lat: terrainLngLat[1], elevation }];
                });
            } else {
                console.error("Map instance ili queryTerrainElevation nisu dostupni.");
            }

            return;
        }

        if (activeMode && activeMode.startsWith('add')) {
            if (activeMode.includes('addPoint')) {
                handleDrawPoint(info, points, setPoints, geometryName, wfsLayers, setAttributeFormOpen);
            } else if (activeMode.includes('addMultiPoint')) {
                handleDrawMultiPoint(info, multiPoints, setMultiPoints, geometryName, wfsLayers, setAttributeFormOpen);
            } else if (activeMode.includes('addLineString')) {
                handleDrawLine(info, addedCoordinate, setAddedCoordinate, wfsLayers);
            } else if (activeMode.includes('addMultiLineString')) {
                handleDrawMultiLine(info, addedCoordinate, setAddedCoordinate, wfsLayers);
            } else if (activeMode.includes('addPolygon')) {
                handleDrawPolygon(info, addedCoordinate, setAddedCoordinate, wfsLayers);
            } else if (activeMode.includes('addMultiPolygon')) {
                handleDrawMultiPolygon(info, addedCoordinate, setAddedCoordinate, wfsLayers);
            }
        }
    };

    useEffect(() => {
        if (!terrainProfilePoints || terrainProfilePoints.length === 0) {
            setTerrainPointsLayer(null);
            return;
        }

        const geojson = {
            type: "FeatureCollection",
            features: terrainProfilePoints.map((point, index) => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [point.lng, point.lat]
                },
                properties: {
                    elevation: point.elevation,
                    id: index
                }
            }))
        };

        setTerrainPointsLayer(new GeoJsonLayer({
            id: 'terrain-points-layer',
            data: geojson,
            pickable: true,
            pointRadiusMinPixels: 5,
            getFillColor: [255, 0, 0, 255],
            getRadius: 10,
            getTooltip: d => `Elevation: ${d.properties.elevation} m`
        }));
    }, [terrainProfilePoints]);

    const handleRightClickMeasure = useCallback(() => {
        rightClick(activeMode, currentMeasureDrawing, setMeasurementResult, setActiveMode);
    }, [activeMode, currentMeasureDrawing, setMeasurementResult, setActiveMode]);

    const handleMouseMove = useCallback((event) => {
        mouseMove(setCurrentCoordinate, event, activeMode, null, wfsLayers, setSnappedVertex, setSnapLayer);
    }, [activeMode, currentMeasureDrawing, wfsLayers]);

    const handleCompleteDrawing = useCallback(() => {
        if (geometryType === 'LineString' && addedCoordinate.length > 1) {
            handleCompleteDrawingLine(addedCoordinate, lines, setLines, geometryName, setAttributeFormOpen);
        } else if (geometryType === 'MultiLineString' && addedCoordinate.length > 1) {
            handleCompleteDrawingMultiLine(addedCoordinate, multiLines, setMultiLines, geometryName, setAttributeFormOpen);
        } else if (geometryType === 'Polygon' && addedCoordinate.length > 2) {
            handleCompleteDrawingPolygon(addedCoordinate, polygons, setPolygons, geometryName, setAttributeFormOpen);
        } else if (geometryType === 'MultiPolygon' && addedCoordinate.length > 2) {
            handleCompleteDrawingMultiPolygon(addedCoordinate, multiPolygons, setMultiPolygons, geometryName, setAttributeFormOpen);
        }

        if (activeMode) {
            const getValueAfterDash = (activeMode) => activeMode.split('-')[1] || '';
            setLayerName(getValueAfterDash);
        }
    }, [
        activeMode,
        addedCoordinate,
        geometryName,
        geometryType,
        lines,
        multiLines,
        multiPolygons,
        polygons,
        setLayerName
    ]);

    const measurePointLayer = new GeoJsonLayer({
        id: 'measure-point-layer',
        data: {
            type: 'FeatureCollection',
            features: currentMeasureDrawing.map((coord, i) => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: coord
                },
                properties: { id: i }
            }))
        },
        pickable: false,
        filled: true,
        stroked: true,
        getPointRadius: 5,
        pointRadiusMinPixels: 5,
        pointRadiusMaxPixels: 10,
        pointRadiusUnits: 'pixels',
        getFillColor: [0, 0, 255, 200],
        getLineColor: [255, 255, 255, 200],
        lineWidthMinPixels: 1,
        visible: true
    });

    useEffect(() => {
        if (activeMode?.startsWith("measure") || activeMode === "regionOfInterest") {
            const newLayer = new GeoJsonLayer({
                id: 'current-measuring-layer',
                data: {
                    type: 'FeatureCollection',
                    features: (activeMode === 'measureArea' || activeMode === 'regionOfInterest') && currentMeasureDrawing?.length > 0
                        ? [{
                            type: 'Feature',
                            geometry: {
                                type: 'Polygon',
                                coordinates: [[...currentMeasureDrawing, currentMeasureDrawing[0]]]
                            }
                        }]
                        : activeMode === 'measureDistance' && currentMeasureDrawing?.length > 0
                            ? [{
                                type: 'Feature',
                                geometry: {
                                    type: 'LineString',
                                    coordinates: currentMeasureDrawing
                                }
                            }]
                            : []
                },
                pickable: false,
                stroked: true,
                filled: true,
                lineWidthMinPixels: 2,
                getLineColor: activeMode === "regionOfInterest" ? [0, 255, 0, 255] : [255, 0, 0, 255],
                getFillColor: activeMode === "regionOfInterest" ? [0, 255, 0, 100] : [255, 0, 0, 100],
                onError: (error) => console.error('GeoJsonLayer error:', error),
            });

            setMeasureLayer(newLayer);
        } else if (!measurementResult) {
            setMeasureLayer(null);
        }
    }, [activeMode, currentMeasureDrawing, measurementResult]);

    const handleFinishRegionOfInterest = async () => {
        await analyzeRoi(
            currentMeasureDrawing,
            setWfsLayers,
            () => wfsLayers,
            setRegionStubovi,
            setRegionTrafostanice,
            setRegionVodovi,
            setHoverInfo
        );
    };

    const handleCancelRegionOfInterest = () => {
        setCurrentMeasureDrawing([]);
        cancelSelectedRoi(setWfsLayers, setRegionStubovi, setRegionTrafostanice, setRegionVodovi, setHoverInfo);
        setRegionStubovi([]);
        setRegionTrafostanice([]);
        setRegionVodovi([]);
    };

    useEffect(() => {
        if (manualCoordinates) {
            handleDrawPoint(manualCoordinates, points, setPoints, "geometrija", wfsLayers, setAttributeFormOpen);
        }
    }, [manualCoordinates]);

    const [selectedNetworkType, setSelectedNetworkType] = useState(null);

    const handleDrawingChange = ({ selectedLayer, coordinates }) => {
        setGeometryName(selectedLayer.geometryName);
        setLayerCRS(selectedLayer.epsgCode);
        setLayerId(selectedLayer.name);
        setSelectedNetworkType(selectedLayer.tip_mreze || null);

        const selectedCleanLayerName = `${selectedLayer.name.replace(/-layer$/, '')}`;
        setExtractedLayerName(selectedCleanLayerName);
        setManualCoordinates(coordinates);
    };

    const handleSaveAttributeForm = (values) => {
        setAttributeValues(values);
        setAttributeFormOpen(false);
    };

    useEffect(() => {
        const layers = createLayers(
            points,
            multiPoints,
            lines,
            multiLines,
            polygons,
            multiPolygons,
            addedCoordinate,
            geometryType,
            geometryName,
            featureProperties,
        );

        setDrawenLayers(layers);
    }, [
        points,
        multiPoints,
        lines,
        multiLines,
        polygons,
        multiPolygons,
        addedCoordinate,
        geometryType,
        geometryName,
        featureProperties
    ]);

    useEffect(() => {
        let timeoutId;

        if (!isAttributeFormOpen && Object.keys(attributeValues).length > 0) {
            handleSave(
                points,
                multiPoints,
                lines,
                multiLines,
                polygons,
                multiPolygons,
                attributeValues,
                layerCRS,
                extractedLayerName,
                setInputUrl,
                setRefresh,
                setCurrentDrawing,
                setPoints,
                setMultiPoints,
                setLines,
                setMultiLines,
                setPolygons,
                setMultiPolygons,
                setPopupInfo,
                setIsCreated
            );

            setLayerName(extractedLayerName);

            timeoutId = setTimeout(() => {
                setDrawenLayers(prevLayers => prevLayers.filter(layer =>
                    layer.id !== "drawn-features-layer" && layer.id !== "current-drawing-layer"
                ));
            }, 500);

            setAddedCoordinate([]);
        } else if (!isAttributeFormOpen) {
            timeoutId = setTimeout(() => {
                setDrawenLayers(prevLayers => prevLayers.filter(layer =>
                    layer.id !== "drawn-features-layer" && layer.id !== "current-drawing-layer"
                ));
            }, 500);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [attributeValues, isAttributeFormOpen]);

    useEffect(() => {
        if (isUpdated && activeMode?.startsWith("update")) {
            setActiveMode("resetovanje");
            setTimeout(() => setActiveMode("update"), 0);
        } else if (isDeleted && activeMode === "delete") {
            setActiveMode("resetovanje");
            setTimeout(() => setActiveMode("delete"), 0);
        }
    }, [isUpdated, isDeleted, activeMode, setActiveMode]);

    useEffect(() => {
        const selectedLayer = wfsLayers?.find(layer => layer.id === layerId);
        let nextFeatureProperties = [];

        if (Array.isArray(selectedLayer?.props?.data?.features) && selectedLayer.props.data.features.length > 0) {
            nextFeatureProperties = Object.keys(selectedLayer.props.data.features[0].properties);
        } else {
            const layerMeta = layerInfo?.find(info => info.name === layerId);

            if (layerMeta?.attributes?.length > 0) {
                nextFeatureProperties = layerMeta.attributes.map(attr => attr.name);
            }
        }

        setFeatureProperties(nextFeatureProperties);
    }, [layerId, wfsLayers, layerInfo]);

    useEffect(() => {
        if (isUpdated || isDeleted) {
            setSelectedFeature(null);
        } else if (
            activeMode !== "select" &&
            activeMode !== "selectRectangle" &&
            activeMode !== "selectCircle" &&
            !(typeof activeMode === "string" && activeMode.includes("update")) &&
            activeMode !== "delete"
        ) {
            setSelectedFeature(null);
        }

        if (
            activeMode !== "selectMultiple" &&
            activeMode !== "selectRectangle" &&
            activeMode !== "selectCircle"
        ) {
            setMultiSelectedFeatures([]);
        }
    }, [activeMode, isUpdated, isDeleted, setSelectedFeature, setMultiSelectedFeatures]);

    useEffect(() => {
        if (activeMode !== "selectRectangle" && activeMode !== "selectCircle") {
            setSelectionDrag(null);
        }
    }, [activeMode]);

    useEffect(() => {
        if (activeMode === 'splitLine' && currentDrawing?.geometry) {
            setEditFeatures({
                type: "FeatureCollection",
                features: [currentDrawing],
            });

            setEditMode(new ModifyMode({ enableMidpoint: true }));
        }
    }, [activeMode, currentDrawing]);

    const editableLayer = new EditableGeoJsonLayer({
        id: 'edit-line-layer',
        data: editFeatures,
        mode: editMode || new ModifyMode(),
        selectedFeatureIndexes: [0],
        onEdit: ({ updatedData }) => {
            setEditFeatures(updatedData);
        },
        getLineColor: [255, 0, 0, 255],
        getLineWidth: 4,
        pickable: true,
        onClick: (info) => {
            if (!editFeatures || !editFeatures.features?.length) return;

            const line = editFeatures.features[0];
            if (line.geometry.type !== "LineString") return;

            const clickedCoord = [info.coordinate[0], info.coordinate[1], 0];
            const coords = [...line.geometry.coordinates];

            let insertIndex = -1;
            let minDist = Infinity;

            for (let i = 0; i < coords.length - 1; i++) {
                const [x1, y1] = coords[i];
                const [x2, y2] = coords[i + 1];

                const dx = x2 - x1;
                const dy = y2 - y1;

                const t = Math.max(
                    0,
                    Math.min(
                        1,
                        ((clickedCoord[0] - x1) * dx + (clickedCoord[1] - y1) * dy) / (dx * dx + dy * dy)
                    )
                );

                const projX = x1 + t * dx;
                const projY = y1 + t * dy;
                const dist = Math.hypot(clickedCoord[0] - projX, clickedCoord[1] - projY);

                if (dist < minDist) {
                    minDist = dist;
                    insertIndex = i + 1;
                }
            }

            if (insertIndex > -1) {
                coords.splice(insertIndex, 0, clickedCoord);

                const updated = {
                    ...editFeatures,
                    features: [{
                        ...line,
                        geometry: {
                            ...line.geometry,
                            coordinates: coords
                        }
                    }]
                };

                setEditFeatures(updated);
            }
        }
    });

    const selectionOverlayData = (() => {
        const mapInstance = mapRef.current?.getMap?.();

        if (!mapInstance || !selectionDrag?.start || !selectionDrag?.end) {
            return { type: "FeatureCollection", features: [] };
        }

        if (selectionDrag.type === "selectRectangle") {
            return createRectangleGeoJson(mapInstance, selectionDrag.start, selectionDrag.end);
        }

        if (selectionDrag.type === "selectCircle") {
            return createCircleGeoJson(mapInstance, selectionDrag.start, selectionDrag.end);
        }

        return { type: "FeatureCollection", features: [] };
    })();

    const selectionOverlayLayer = new GeoJsonLayer({
        id: "selection-area-overlay-layer",
        data: selectionOverlayData,
        pickable: false,
        stroked: true,
        filled: true,
        getFillColor: [0, 255, 255, 35],
        getLineColor: [0, 255, 255, 220],
        lineWidthMinPixels: 2,
    });

    const decksLayers = [
        hoverLayer,
        ...(activeMode && activeMode.includes('add') ? [snapLayer] : []),
        ...wfsLayers,
        multipleSelectedFeatureLayer,
        ...wmsLayers,
        measurePointLayer,
        measureLayer,
        ...drawenLayers,
        ...(Array.isArray(lasLayers) ? lasLayers : [lasLayers]),
        ...(terrainPointsLayer ? [terrainPointsLayer] : []),
        ...(editFeatures?.features?.length > 0 ? [editableLayer] : []),
        selectionOverlayLayer
    ].filter(layer => layer !== undefined && layer !== null);

    useEffect(() => {
        setIsLayersExists(Array.isArray(wfsLayers) && wfsLayers.length > 0);
    }, [wfsLayers]);

    const handleMouseMoveOptimized = useCallback((event) => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        animationFrameRef.current = requestAnimationFrame(() => {
            verteksRef.current = event;
            handleMouseMove(event);
        });
    }, [handleMouseMove]);

    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    const debouncedSetViewState = useCallback(
        debounce((nextViewState) => {
            setViewState(nextViewState);
        }, 200),
        [setViewState]
    );

    useLayoutEffect(() => {
        const resizeMapAndLayers = () => {
            requestAnimationFrame(() => {
                if (mapRef.current?.getMap) {
                    mapRef.current.getMap().resize();
                }

                if (deckRef.current?.deck) {
                    deckRef.current.deck.redraw(true);
                }

                setViewState(prev => ({ ...prev }));
            });
        };

        resizeMapAndLayers();

        const timeout1 = setTimeout(resizeMapAndLayers, 100);
        const timeout2 = setTimeout(resizeMapAndLayers, 300);
        const timeout3 = setTimeout(resizeMapAndLayers, 600);

        return () => {
            clearTimeout(timeout1);
            clearTimeout(timeout2);
            clearTimeout(timeout3);
        };
    }, [
        isRightSidebarCollapsed,
        isMenuOpen,
        isLeftSidebarCollapsed,
        isTrafoActive,
        isExpanded,
        isCompareMode
    ]);

    const handleUndoDrawingLocal = () => {
        if (addedCoordinate.length > 0) {
            setAddedCoordinate(prev => prev.slice(0, -1));
        }
    };

    const handleCancelDrawing = () => {
        setPoints([]);
        setMultiPoints([]);
        setLines([]);
        setMultiLines([]);
        setPolygons([]);
        setMultiPolygons([]);
        setAddedCoordinate([]);
        setCurrentDrawing([]);
        setDrawenLayers([]);
        setActiveMode(null);
        setAttributeFormOpen(false);
        setManualCoordinates(null);
    };

    const saveSplitLine = () => {
        if (!editFeatures?.features?.length) return;

        const geometry = editFeatures.features[0].geometry;

        handleUpdateGeometryWrapper(geometry);
        setEditFeatures(null);
        setActiveMode(null);
    };

    return (
        <main
            ref={mainContentRef}
            className="main-content"
            onContextMenu={(e) => {
                e.preventDefault();
                handleRightClickMeasure();
            }}
        >
            <DeckGL
                key={deckKey}
                glOptions={{ preserveDrawingBuffer: true }}
                onWebGLInitialized={(gl) => {
                    gl.getExtension('OES_element_index_uint');
                    gl.getExtension('EXT_color_buffer_float');
                }}
                onWebGLContextLost={(event) => {
                    console.warn('WebGL context lost!', event);
                    event.preventDefault();
                }}
                ref={deckRef}
                viewState={viewState}
                controller={!isCompareMode}
                layers={decksLayers}
                style={{ width: '100%', height: '100%' }}
                onViewStateChange={({ viewState }) => {
                    setViewState(prevState => ({
                        ...viewState,
                        is3D: prevState.is3D
                    }));
                }}
                onClick={handleClickOnMap}
                onHover={(info) => {
                    handleAreaSelectionHover(info);
                    handleMouseMove(info);
                }}
                getCursor={() => isAreaSelectMode ? "crosshair" : "inherit"}
                parameters={{ preserveDrawingBuffer: true }}
            >
                <Map
                    mapStyle={mapStyle}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    ref={mapRef}
                    terrain={{ source: "mapbox-dem", exaggeration: 1.5 }}
                    preserveDrawingBuffer={true}
                >
                    {marker && (
                        <Marker latitude={marker.latitude} longitude={marker.longitude}>
                            <FaMapMarkerAlt
                                size={40}
                                color="red"
                                style={{ transform: 'translate(-50%, -100%)' }}
                            />
                        </Marker>
                    )}
                </Map>
            </DeckGL>

            {isCompareMode && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 40,
                    }}
                >
                    <CompareLayers
                        checkingLayers={checkedLayers}
                        viewState={viewState}
                        handleMouseMove={handleMouseMoveOptimized}
                        onMapClick={handleClickOnMap}
                        setViewState={setViewState}
                        mapStyle={mapStyle}
                    />
                </div>
            )}

            <HoverInfo
                hoverInfo={hoverInfo}
                substationOptions={substationOptions}
                lasLayers={lasLayers}
            />

            {hasAnyAccess && (
                <DrawingToolbar
                    layerInfo={layerInfo}
                    activeMode={activeMode}
                    setActiveMode={setActiveMode}
                    onChange={handleDrawingChange}
                    viewState={viewState}
                    setViewState={setViewState}
                    setGeometryType={setGeometryType}
                    handleCompleteDrawing={handleCompleteDrawing}
                    handleUndoDrawing={handleUndoDrawingLocal}
                    handleFinish={handleFinishRegionOfInterest}
                    handleCancel={handleCancelRegionOfInterest}
                    terrainProfilePoints={terrainProfilePoints}
                    setTerrainProfilePoints={setTerrainProfilePoints}
                    isLayersExists={isLayersExists}
                    showSearch={showSearch}
                    setShowSearch={setShowSearch}
                    handleCancelDrawing={handleCancelDrawing}
                    saveSplitLine={saveSplitLine}
                    handleClearSelectedFeatures={handleClearSelectedFeatures}
                />
            )}

            {isAttributeFormOpen && (
                <AttributeForm
                    featureProperties={featureProperties}
                    onSubmit={(values) => {
                        handleSaveAttributeForm(values);
                    }}
                    isOpen={isAttributeFormOpen}
                    onRequestClose={() => {
                        setAttributeFormOpen(false);
                        handleCancelDrawing();
                    }}
                    typeName={`elektro:${extractedLayerName}`}
                    ojInfo={ojInfo}
                    selectedNetworkType={selectedNetworkType}
                />
            )}

            {showSearch && (
                <MapSearch
                    setViewState={setViewState}
                    setMarker={setMarker}
                    marker={marker}
                />
            )}

            <ZoomControl zoom={viewState.zoom} setViewState={setViewState} />
        </main>
    );
};

export default MainContent;